<template>
  <div class="container-fluid zona-form-page">
    <div class="row mb-3 align-items-center">
      <div class="col">
        <button class="btn btn-link pl-0" @click="goBack">← Volver</button>
        <h4 class="mb-1">{{ isEdit ? 'Editar zona' : 'Agregar zona' }}</h4>
        <p class="text-muted mb-0">
          Busca una dirección o haz clic en el mapa para agregar vértices del polígono (mín. 3).
          Horario en zona Chile (America/Santiago).
        </p>
      </div>
    </div>

    <div v-if="errorMessage" class="alert alert-danger">{{ errorMessage }}</div>
    <div v-if="loadingZone" class="text-center py-5 text-muted">Cargando zona…</div>

    <div v-else class="row">
      <div class="col-lg-4 mb-3">
        <div class="card h-100">
          <div class="card-body">
            <form @submit.prevent="save">
              <div class="form-group">
                <label>Nombre zona</label>
                <input v-model="form.name" class="form-control" required maxlength="120" />
              </div>

              <div class="form-group">
                <label>Color zona</label>
                <div class="d-flex align-items-center">
                  <input v-model="form.color" type="color" class="form-control form-control-color mr-2" />
                  <input v-model="form.color" class="form-control" maxlength="20" />
                </div>
              </div>

              <div class="form-group">
                <label>Tipo de bono</label>
                <select v-model="form.bonusType" class="form-control">
                  <option value="porcentaje">Porcentaje (%)</option>
                  <option value="cantidad">Cantidad (CLP)</option>
                </select>
              </div>

              <div class="form-group">
                <label>
                  {{ form.bonusType === 'cantidad' ? 'Cantidad del bono (CLP)' : 'Porcentaje del bono (%)' }}
                </label>
                <input
                  v-model.number="form.bonusValue"
                  type="number"
                  min="0"
                  step="0.01"
                  class="form-control"
                  required
                />
              </div>

              <div class="row">
                <div class="col-6">
                  <div class="form-group">
                    <label>Hora inicio</label>
                    <input v-model="form.startTime" type="time" class="form-control" required />
                  </div>
                </div>
                <div class="col-6">
                  <div class="form-group">
                    <label>Hora fin</label>
                    <input v-model="form.endTime" type="time" class="form-control" required />
                  </div>
                </div>
              </div>
              <p v-if="crossesMidnight" class="small text-warning mb-3">
                Esta zona cruza medianoche: activa desde {{ form.startTime }} hasta {{ form.endTime }} del día siguiente.
              </p>

              <div class="form-group form-check">
                <input id="zoneActive" v-model="form.isActive" type="checkbox" class="form-check-input" />
                <label class="form-check-label" for="zoneActive">Zona activa</label>
              </div>

              <div class="mb-3">
                <label class="d-block">Coordenadas ({{ pins.length }})</label>
                <div v-if="!pins.length" class="text-muted small">Sin vértices aún.</div>
                <ul v-else class="list-unstyled small mb-2 pin-list">
                  <li v-for="(pin, index) in pins" :key="index" class="d-flex justify-content-between mb-1">
                    <span>{{ index + 1 }}. {{ pin.latitude.toFixed(5) }}, {{ pin.longitude.toFixed(5) }}</span>
                    <button type="button" class="btn btn-sm btn-link text-danger p-0" @click="removePin(index)">
                      Quitar
                    </button>
                  </li>
                </ul>
                <button type="button" class="btn btn-sm btn-outline-secondary mr-1" @click="undoPin" :disabled="!pins.length">
                  Deshacer último
                </button>
                <button type="button" class="btn btn-sm btn-outline-danger" @click="clearPins" :disabled="!pins.length">
                  Limpiar
                </button>
              </div>

              <button type="submit" class="btn btn-primary btn-block" :disabled="saving">
                {{ saving ? 'Guardando…' : 'Guardar zona' }}
              </button>
            </form>
          </div>
        </div>
      </div>

      <div class="col-lg-8 mb-3">
        <div class="card">
          <div class="card-body p-2">
            <div class="search-box mb-2">
              <input
                v-model="searchQuery"
                class="form-control"
                placeholder="Buscar dirección (Mapbox)…"
                @input="onSearchInput"
                @keydown.enter.prevent="pickFirstSuggestion"
              />
              <ul v-if="suggestions.length" class="suggestions list-unstyled mb-0">
                <li
                  v-for="(item, idx) in suggestions"
                  :key="item.id || idx"
                  @click="selectSuggestion(item)"
                >
                  {{ item.place_name }}
                </li>
              </ul>
            </div>
            <div ref="mapContainer" class="zona-map" />
            <p class="small text-muted mb-0 mt-2 px-1">
              Clic en el mapa = agregar pin. Arrastra los marcadores para ajustar el polígono.
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { createZone, fetchZone, updateZone } from '@/services/zonesApi';

const DEFAULT_CENTER = [-72.9369, -41.4717]; // Puerto Montt
const MAPBOX_TOKEN =
  process.env.VUE_APP_MAPBOX_TOKEN ||
  'pk.eyJ1IjoianVhbmd0ZWMiLCJhIjoiY21sem1wZWd5MDhvdTNlcHpvc2Jsc2NxaiJ9.Etei9kPSkrgMZW1bbSXbug';

export default {
  name: 'ZonaFormPage',
  data() {
    return {
      loadingZone: false,
      saving: false,
      errorMessage: '',
      searchQuery: '',
      suggestions: [],
      searchTimer: null,
      map: null,
      markers: [],
      pins: [],
      form: {
        name: '',
        color: '#1E3A8A',
        bonusType: 'porcentaje',
        bonusValue: 10,
        startTime: '22:00',
        endTime: '06:00',
        isActive: true,
      },
    };
  },
  computed: {
    isEdit() {
      return Boolean(this.$route.params.id);
    },
    crossesMidnight() {
      return this.form.endTime && this.form.startTime && this.form.endTime < this.form.startTime;
    },
  },
  watch: {
    'form.color'() {
      this.redrawPolygon();
    },
    pins: {
      deep: true,
      handler() {
        this.syncMarkers();
        this.redrawPolygon();
      },
    },
  },
  async mounted() {
    if (this.isEdit) {
      await this.loadZone(this.$route.params.id);
    }
    this.$nextTick(() => this.initMap());
  },
  beforeUnmount() {
    this.destroyMap();
    if (this.searchTimer) clearTimeout(this.searchTimer);
  },
  methods: {
    goBack() {
      this.$router.push({ name: 'zonas' });
    },
    coordinatesFromZone(zone) {
      const coords = zone?.coordinates;
      if (!coords || typeof coords !== 'object') return [];
      return Object.keys(coords)
        .sort((a, b) => Number(a) - Number(b))
        .map((key) => ({
          latitude: Number(coords[key].latitude),
          longitude: Number(coords[key].longitude),
        }))
        .filter((p) => Number.isFinite(p.latitude) && Number.isFinite(p.longitude));
    },
    async loadZone(id) {
      this.loadingZone = true;
      this.errorMessage = '';
      try {
        const zone = await fetchZone(id);
        this.form = {
          name: zone.name || '',
          color: zone.color || '#1E3A8A',
          bonusType: zone.bonusType || 'porcentaje',
          bonusValue: Number(zone.bonusValue ?? zone.percentage ?? 0),
          startTime: zone.startTime || '00:00',
          endTime: zone.endTime || '23:59',
          isActive: zone.isActive !== false,
        };
        this.pins = this.coordinatesFromZone(zone);
      } catch (e) {
        this.errorMessage = e.message || 'No se pudo cargar la zona';
      } finally {
        this.loadingZone = false;
      }
    },
    initMap() {
      if (!this.$refs.mapContainer || this.map) return;
      mapboxgl.accessToken = MAPBOX_TOKEN;
      const first = this.pins[0];
      this.map = new mapboxgl.Map({
        container: this.$refs.mapContainer,
        style: 'mapbox://styles/mapbox/streets-v12',
        center: first ? [first.longitude, first.latitude] : DEFAULT_CENTER,
        zoom: first ? 13 : 12,
      });
      this.map.addControl(new mapboxgl.NavigationControl(), 'top-right');
      this.map.on('load', () => {
        this.ensurePolygonLayers();
        this.syncMarkers();
        this.redrawPolygon();
        if (this.pins.length >= 2) this.fitToPins();
      });
      this.map.on('click', (e) => {
        this.addPin(e.lngLat.lat, e.lngLat.lng);
      });
    },
    destroyMap() {
      this.markers.forEach((m) => m.remove());
      this.markers = [];
      if (this.map) {
        this.map.remove();
        this.map = null;
      }
    },
    ensurePolygonLayers() {
      if (!this.map.getSource('zone-poly')) {
        this.map.addSource('zone-poly', {
          type: 'geojson',
          data: { type: 'FeatureCollection', features: [] },
        });
        this.map.addLayer({
          id: 'zone-fill',
          type: 'fill',
          source: 'zone-poly',
          paint: {
            'fill-color': this.form.color || '#1E3A8A',
            'fill-opacity': 0.25,
          },
        });
        this.map.addLayer({
          id: 'zone-line',
          type: 'line',
          source: 'zone-poly',
          paint: {
            'line-color': this.form.color || '#1E3A8A',
            'line-width': 2,
          },
        });
      }
    },
    polygonGeoJson() {
      if (this.pins.length < 3) {
        return { type: 'FeatureCollection', features: [] };
      }
      const ring = this.pins.map((p) => [p.longitude, p.latitude]);
      const first = ring[0];
      const last = ring[ring.length - 1];
      if (first[0] !== last[0] || first[1] !== last[1]) {
        ring.push([...first]);
      }
      return {
        type: 'FeatureCollection',
        features: [
          {
            type: 'Feature',
            properties: {},
            geometry: { type: 'Polygon', coordinates: [ring] },
          },
        ],
      };
    },
    redrawPolygon() {
      if (!this.map || !this.map.getSource('zone-poly')) return;
      this.map.getSource('zone-poly').setData(this.polygonGeoJson());
      if (this.map.getLayer('zone-fill')) {
        this.map.setPaintProperty('zone-fill', 'fill-color', this.form.color || '#1E3A8A');
      }
      if (this.map.getLayer('zone-line')) {
        this.map.setPaintProperty('zone-line', 'line-color', this.form.color || '#1E3A8A');
      }
    },
    syncMarkers() {
      if (!this.map) return;
      this.markers.forEach((m) => m.remove());
      this.markers = this.pins.map((pin, index) => {
        const el = document.createElement('div');
        el.className = 'zona-pin-marker';
        el.style.background = this.form.color || '#1E3A8A';
        el.textContent = String(index + 1);
        const marker = new mapboxgl.Marker({ element: el, draggable: true })
          .setLngLat([pin.longitude, pin.latitude])
          .addTo(this.map);
        marker.on('dragend', () => {
          const { lng, lat } = marker.getLngLat();
          this.pins.splice(index, 1, { latitude: lat, longitude: lng });
        });
        return marker;
      });
    },
    addPin(lat, lng) {
      this.pins.push({ latitude: lat, longitude: lng });
    },
    removePin(index) {
      this.pins.splice(index, 1);
    },
    undoPin() {
      this.pins.pop();
    },
    clearPins() {
      this.pins = [];
    },
    fitToPins() {
      if (!this.map || !this.pins.length) return;
      const bounds = new mapboxgl.LngLatBounds();
      this.pins.forEach((p) => bounds.extend([p.longitude, p.latitude]));
      this.map.fitBounds(bounds, { padding: 60, maxZoom: 15 });
    },
    onSearchInput() {
      if (this.searchTimer) clearTimeout(this.searchTimer);
      const q = this.searchQuery.trim();
      if (q.length < 3) {
        this.suggestions = [];
        return;
      }
      this.searchTimer = setTimeout(() => this.searchAddress(q), 350);
    },
    async searchAddress(query) {
      try {
        const url =
          `https://api.mapbox.com/geocoding/v5/mapbox.places/` +
          `${encodeURIComponent(query)}.json` +
          `?access_token=${MAPBOX_TOKEN}` +
          `&country=cl&language=es&limit=5` +
          `&proximity=${DEFAULT_CENTER[0]},${DEFAULT_CENTER[1]}`;
        const res = await fetch(url);
        const data = await res.json();
        this.suggestions = Array.isArray(data.features) ? data.features : [];
      } catch (_) {
        this.suggestions = [];
      }
    },
    pickFirstSuggestion() {
      if (this.suggestions[0]) this.selectSuggestion(this.suggestions[0]);
    },
    selectSuggestion(feature) {
      const [lng, lat] = feature.center || [];
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
      this.searchQuery = feature.place_name || '';
      this.suggestions = [];
      this.addPin(lat, lng);
      if (this.map) {
        this.map.flyTo({ center: [lng, lat], zoom: 14 });
      }
    },
    async save() {
      this.errorMessage = '';
      if (!this.form.name.trim()) {
        this.errorMessage = 'Ingresa el nombre de la zona';
        return;
      }
      if (this.pins.length < 3) {
        this.errorMessage = 'Agrega al menos 3 pines en el mapa';
        return;
      }
      this.saving = true;
      try {
        const body = {
          name: this.form.name.trim(),
          color: this.form.color,
          bonusType: this.form.bonusType,
          bonusValue: Number(this.form.bonusValue),
          startTime: this.form.startTime,
          endTime: this.form.endTime,
          isActive: !!this.form.isActive,
          coordinates: this.pins.map((p) => ({
            latitude: p.latitude,
            longitude: p.longitude,
          })),
        };
        if (this.isEdit) {
          await updateZone(this.$route.params.id, body);
        } else {
          await createZone(body);
        }
        this.$router.push({ name: 'zonas', query: { saved: '1' } });
      } catch (e) {
        this.errorMessage = e.message || 'Error al guardar la zona';
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
.zona-map {
  width: 100%;
  height: 560px;
  border-radius: 8px;
  overflow: hidden;
}
.search-box {
  position: relative;
}
.suggestions {
  position: absolute;
  z-index: 20;
  left: 0;
  right: 0;
  top: 100%;
  background: #fff;
  border: 1px solid #dde;
  border-radius: 6px;
  max-height: 220px;
  overflow: auto;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.08);
}
.suggestions li {
  padding: 8px 12px;
  cursor: pointer;
  font-size: 13px;
}
.suggestions li:hover {
  background: #f3f6ff;
}
.pin-list {
  max-height: 160px;
  overflow: auto;
}
.form-control-color {
  width: 48px;
  padding: 2px;
}
</style>

<style>
.zona-pin-marker {
  width: 26px;
  height: 26px;
  border-radius: 50%;
  color: #fff;
  font-size: 12px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px solid #fff;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.35);
  cursor: grab;
}
</style>
